import type { CompanyAccess, CompanyBlockReason } from '../../../services/CompanyAccessService'
import type { CompanyAccessStatus } from '../../../stores/companyAccessStore'

/**
 * Suspended companies: a new tab is closed unless added here. Decide explicitly.
 * See vault `Components-Company-Management.md` → 'Suspended companies and new tabs'.
 *
 * Default-deny (SC-409): while the signed-in user's company is suspended or being deleted,
 * every MainMenu section and sidebar item whose id is NOT listed here is disabled in the
 * sidebar and renders `CompanyDisabledScreen` when reached by URL. `profile` also covers the
 * `/profile/edit` dialog, which maps to the same section. `whatsNew` and `feedback` are
 * sidebar actions (they open a drawer), not sections.
 */
export const SECTIONS_AVAILABLE_WHEN_COMPANY_SUSPENDED = ['profile', 'whatsNew', 'feedback'] as const

export type SuspendedCompanyNavItemId =
  | 'publicLobby'
  | 'myRooms'
  | 'meetings'
  | 'profile'
  | 'whatsNew'
  | 'feedback'

/**
 * Sidebar items while the gate is on, in sidebar order. A MainMenu item that is not listed
 * here is hidden while suspended (My Company, Create Company and Admin, deliberately).
 * Being listed does not open an item: only `SECTIONS_AVAILABLE_WHEN_COMPANY_SUSPENDED` does.
 */
export const MAIN_MENU_NAV_ITEMS_WHEN_SUSPENDED: ReadonlyArray<{
  id: SuspendedCompanyNavItemId
  label: string
}> = [
  { id: 'publicLobby', label: 'Dashboard' },
  { id: 'myRooms', label: 'Rooms' },
  { id: 'meetings', label: 'Meetings' },
  { id: 'profile', label: 'My Profile' },
  { id: 'whatsNew', label: "What's new" },
  { id: 'feedback', label: 'Leave Feedback' },
]

export function isAvailableWhenCompanySuspended(sectionOrItemId: string): boolean {
  return (SECTIONS_AVAILABLE_WHEN_COMPANY_SUSPENDED as readonly string[]).includes(sectionOrItemId)
}

export function suspendedCompanyNavTooltip(companyName: string | null): string {
  return `Unavailable while ${companyName?.trim() || 'your company'} is suspended`
}

/**
 * - pending: not known yet (no answer, or blocked while admin capabilities are still
 *   loading). Render as before the gate, but do not act on deep links or landing redirects.
 * - open: not blocked, a platform admin, or the access check failed with nothing known
 *   (the UI fails open; the backend still refuses).
 * - gated: blocked and not a platform admin.
 */
export type CompanyAccessGateState = 'pending' | 'open' | 'gated'

export interface CompanyAccessGate {
  state: CompanyAccessGateState
  companyId: string | null
  companyName: string | null
  reason: CompanyBlockReason | null
}

export function resolveCompanyAccessGate(input: {
  access: CompanyAccess | null
  status: CompanyAccessStatus
  accessUserId: string | null
  currentUserId: string | null
  adminCapabilitiesVerified: boolean
  isElevatedAdmin: boolean
}): CompanyAccessGate {
  const sameUser = !!input.currentUserId && input.accessUserId === input.currentUserId
  const access = sameUser ? input.access : null
  const base = {
    companyId: access?.companyId ?? null,
    companyName: access?.companyName ?? null,
    reason: access?.reason ?? null,
  }

  if (!access) {
    // A failed first check fails open; anything else is still on its way.
    const failed = sameUser && input.status === 'error'
    return { ...base, state: failed ? 'open' : 'pending' }
  }
  if (!access.blocked) return { ...base, state: 'open' }
  // Platform admins are never gated, including one who belongs to a suspended company.
  if (input.isElevatedAdmin) return { ...base, state: 'open' }
  if (!input.adminCapabilitiesVerified) return { ...base, state: 'pending' }
  return { ...base, state: 'gated' }
}
